import * as i18n from 'i18next';
import { makeNewContainer } from 'ui/editor/makeNewContainer';
import { makeNewAsset } from 'ui/editor/makeNewAsset';
import { DeviceType, AssetType } from 'shared/models';
import { merge } from 'lodash';
import { calcMinPITextHeight } from 'logic/calcMinPITextHeight';

export function makeEcsContainers(width: number, height: number, deviceType: DeviceType | null, isNorthAmerica: boolean, t: i18n.TFunction) {
    return width >= height
        ? makeHorizontalEcsContainers(width, height, deviceType, isNorthAmerica, t)
        : makeVerticalEcsContainers(width, height, deviceType, isNorthAmerica, t);
}

function makeHorizontalEcsContainers(width: number, height: number, deviceType: DeviceType | null, isNorthAmerica: boolean, t: i18n.TFunction) {
    const margin = .05;
    const hSplit = .4; // the PI takes the left side of this split; the chat/instructions/status takes the right side of this split
    const vSplit = .7; // the chat takes the top side of this split; the instructions and status takes the bottom side of this split
    const statusMessageHeight = .08;

    const minTextHeight = calcMinPITextHeight(deviceType, false, isNorthAmerica, true);

    const statusMessageAsset = merge({}, makeNewAsset(AssetType.Text, width, height), {
        id: 'ecs-status-message-text',
        properties: {
            color: '#000',
            text: '1-way video messaging session established', // KILL
            halign: 'center',
            valign: 'center',
            size: .04,
        },
    });
    const statusMessageContainer = makeNewContainer({
        id: 'ecs-status-message',
        top: 1 - statusMessageHeight,
        left: 0,
        width: 1,
        height: statusMessageHeight,
        displayName: t('THEME_EDITOR.ECS.STATUS_AREA_NAME'),
        type: 'ecs',
        backgroundColor: 'rgb(255,255,0)',
        assetOrder: [ statusMessageAsset.id ],
        locked: true,
    });

    const backgroundAsset = makeNewAsset(AssetType.Image, width, height);
    // TODO: how to get the image into the asset? Upload to S3 and hardcode here...? For now use a solid colour
    const backgroundContainer = makeNewContainer({
        id: 'ecs-background',
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        displayName: t('THEME_EDITOR.ECS.BACKGROUND_AREA_NAME'),
        type: 'ecs',
        backgroundColor: '#007FFF',
        assetOrder: [ backgroundAsset.id ],
        locked: true,
    });

    // Calculate the height so that it makes a square (so the up and down indicators overlap)
    const directionWidth = hSplit * .6;
    const directionHeight = directionWidth / height * width;
    const directionAsset = makeNewAsset(AssetType.Direction, width, height);
    const directionContainer = makeNewContainer({
        id: 'ecs-direction',
        top: Math.max(0, .5 - directionHeight),
        left: (hSplit - directionWidth) / 2,
        width: directionWidth,
        height: directionHeight,
        displayName: t('THEME_EDITOR.PI.DIRECTION_AREA_NAME'),
        type: 'ecs',
        assetOrder: [ directionAsset.id ],
        locked: true,
    });

    const floorAsset = merge({}, makeNewAsset(AssetType.Floor, width, height, 0, 0, minTextHeight), {
        properties: {
            valign: 'top',
        },
    });
    const floorContainer = makeNewContainer({
        id: 'ecs-floor',
        top: .5,
        left: 0,
        width: hSplit,
        height: .5 - margin,
        displayName: t('THEME_EDITOR.PI.FLOOR_AREA_NAME'),
        type: 'ecs',
        assetOrder: [ floorAsset.id ],
        locked: true,
    });

    const instructionsYesAsset = merge({}, makeNewAsset(AssetType.Text, width, height), {
        properties: {
            valign: 'top',
            halign: 'center',
            color: '#000',
            text: 'Press for YES',
            size: .05,
        },
    });
    const instructionsYesContainer = makeNewContainer({
        id: 'ecs-instructions-yes',
        top: vSplit,
        left: hSplit,
        width: (1 - hSplit) / 2,
        height: 1 - vSplit - statusMessageContainer.height,
        displayName: t('THEME_EDITOR.ECS.YES_AREA_NAME'),
        type: 'ecs',
        backgroundColor: '#bfbfbf',
        borderColor: '#bfbfbf',
        borderThickness: Math.round(.015 * height),
        assetOrder: [ instructionsYesAsset.id ],
        locked: true,
    });

    const instructionsYesSymbolAsset = merge({}, makeNewAsset(AssetType.Symbol, width, height), {
        properties: {
            valign: 'center',
            halign: 'center',
            color: '#000',
            symbol: 'door-open',
        },
    });
    const instructionsYesSymbolContainer = makeNewContainer({
        id: 'ecs-instructions-yes-symbol',
        top: vSplit + .08,
        left: hSplit,
        width: (1 - hSplit) / 2,
        height: 1 - vSplit - statusMessageContainer.height - .1,
        displayName: t('THEME_EDITOR.ECS.YES_SYMBOL_AREA_NAME'),
        type: 'ecs',
        assetOrder: [ instructionsYesSymbolAsset.id ],
        locked: true,
    });

    const instructionsNoAsset = merge({}, makeNewAsset(AssetType.Text, width, height), {
        properties: {
            valign: 'top',
            halign: 'center',
            color: '#000',
            text: 'Press for NO',
            size: .05,
        },
    });
    const instructionsNoContainer = makeNewContainer({
        id: 'ecs-instructions-no',
        top: vSplit,
        left: 1 - (1 - hSplit) / 2,
        width: (1 - hSplit) / 2,
        height: 1 - vSplit - statusMessageContainer.height,
        displayName: t('THEME_EDITOR.ECS.NO_AREA_NAME'),
        type: 'ecs',
        backgroundColor: '#bfbfbf',
        borderColor: '#bfbfbf',
        borderThickness: Math.round(.015 * height),
        assetOrder: [ instructionsNoAsset.id ],
        locked: true,
    });

    const instructionsNoSymbolAsset = merge({}, makeNewAsset(AssetType.Symbol, width, height), {
        properties: {
            valign: 'center',
            halign: 'center',
            color: '#000',
            symbol: 'door-close',
        },
    });
    const instructionsNoSymbolContainer = makeNewContainer({
        id: 'ecs-instructions-no-symbol',
        top: vSplit + .08,
        left: hSplit + (1 - hSplit) / 2,
        width: (1 - hSplit) / 2,
        height: 1 - vSplit - statusMessageContainer.height - .1,
        displayName: t('THEME_EDITOR.ECS.NO_SYMBOL_AREA_NAME'),
        type: 'ecs',
        assetOrder: [ instructionsNoSymbolAsset.id ],
        locked: true,
    });

    const messagesContainer = makeNewContainer({
        id: 'ecs-messages',
        top: 0,
        left: hSplit,
        width: 1 - hSplit,
        height: vSplit,
        displayName: t('THEME_EDITOR.ECS.MESSAGES_AREA_NAME'),
        type: 'ecs',
        backgroundColor: '#fff',
        assetOrder: [],
        locked: true,
    });


    /* TODO: Emergency broadcast containers
        <div id="2eb665bc-37b8-45d0-806d-a4316d0f8a78" class="container ts-fade container-fake-broadcast" style="top:0px;left:280px;width:744px;height:80px;background:#000;color:#fff; font-size: 42px; line-height: 80px; z-index: 100; opacity: 0; font-family: sans-serif; text-align: center; font-weight: 700;">EMERGENCY ALERT</div>
        <div id="45a05685-83db-44d5-8101-d3dc99dae649" class="container ts-fade container-fake-broadcast" style="top:80px;left:280px;width:744px;height:520px; background:#c00; color:#fff; z-index: 100; opacity: 0; white-space: pre-wrap; font-size: 32px; text-align: center; font-family: sans-serif; padding: 30px; line-height: 38px;"></div>
    */

    return {
        ecsContainers: [
            backgroundContainer,
            statusMessageContainer,
            directionContainer,
            floorContainer,
            instructionsYesContainer,
            instructionsYesSymbolContainer,
            instructionsNoContainer,
            instructionsNoSymbolContainer,
            messagesContainer,
        ],
        ecsAssets: {
            [statusMessageAsset.id]: statusMessageAsset,
            [directionAsset.id]: directionAsset,
            [floorAsset.id]: floorAsset,
            [backgroundAsset.id]: backgroundAsset,
            [instructionsYesAsset.id]: instructionsYesAsset,
            [instructionsYesSymbolAsset.id]: instructionsYesSymbolAsset,
            [instructionsNoAsset.id]: instructionsNoAsset,
            [instructionsNoSymbolAsset.id]: instructionsNoSymbolAsset,
        },
    };
}


function makeVerticalEcsContainers(width: number, height: number, deviceType: DeviceType | null, isNorthAmerica: boolean, t: i18n.TFunction) {
    const piHeight = .3;
    const messagesHeight = .45;
    const instructionsHeight = .18;
    const statusHeight = .07;

    const minTextHeight = calcMinPITextHeight(deviceType, true, isNorthAmerica, true);

    const statusMessageAsset = merge({}, makeNewAsset(AssetType.Text, width, height), {
        id: 'ecs-status-message-text',
        properties: {
            color: '#000',
            text: '1-way video messaging session established', // KILL
            halign: 'center',
            valign: 'center',
            size: .03,
        },
    });
    const statusMessageContainer = makeNewContainer({
        id: 'ecs-status-message',
        top: 1 - statusHeight,
        left: 0,
        width: 1,
        height: statusHeight,
        displayName: t('THEME_EDITOR.ECS.STATUS_AREA_NAME'),
        type: 'ecs',
        backgroundColor: 'rgb(255,255,0)',
        assetOrder: [ statusMessageAsset.id ],
        locked: true,
    });

    const backgroundAsset = makeNewAsset(AssetType.Image, width, height);
    // TODO: how to get the image into the asset? Upload to S3 and hardcode here...? For now use a solid colour
    const backgroundContainer = makeNewContainer({
        id: 'ecs-background',
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        displayName: t('THEME_EDITOR.ECS.BACKGROUND_AREA_NAME'),
        type: 'ecs',
        backgroundColor: '#007FFF',
        assetOrder: [ backgroundAsset.id ],
        locked: true,
    });

    // Calculate the height so that it makes a square (so the up and down indicators overlap)
    const directionMargin = .04;
    const directionWidth = .35 - 2 * directionMargin;
    const directionHeight = Math.min(piHeight - 2 * directionMargin, directionWidth / height * width);
    const directionAsset = makeNewAsset(AssetType.Direction, width, height);
    const directionContainer = makeNewContainer({
        id: 'ecs-direction',
        top: Math.max(0, (piHeight - directionHeight) / 2),
        left: 1 - directionWidth - directionMargin,
        width: directionWidth,
        height: directionHeight,
        displayName: t('THEME_EDITOR.PI.DIRECTION_AREA_NAME'),
        type: 'ecs',
        assetOrder: [ directionAsset.id ],
        locked: true,
    });

    const floorAsset = merge({}, makeNewAsset(AssetType.Floor, width, height, 0, 0, minTextHeight), {
        properties: {
            valign: 'center',
            halign: 'center',
        },
    });
    const floorContainer = makeNewContainer({
        id: 'ecs-floor',
        top: 0,
        left: 0,
        width: .65 + directionMargin,
        height: piHeight,
        displayName: t('THEME_EDITOR.PI.FLOOR_AREA_NAME'),
        type: 'ecs',
        assetOrder: [ floorAsset.id ],
        locked: true,
    });

    const instructionsYesAsset = merge({}, makeNewAsset(AssetType.Text, width, height), {
        properties: {
            valign: 'top',
            halign: 'center',
            color: '#000',
            text: 'Press for YES',
            size: .03,
        },
    });
    const instructionsYesContainer = makeNewContainer({
        id: 'ecs-instructions-yes',
        top: piHeight + messagesHeight,
        left: 0,
        width: .5,
        height: instructionsHeight,
        displayName: t('THEME_EDITOR.ECS.YES_AREA_NAME'),
        type: 'ecs',
        backgroundColor: '#bfbfbf',
        borderColor: '#bfbfbf',
        borderThickness: Math.round(.015 * height),
        assetOrder: [ instructionsYesAsset.id ],
        locked: true,
    });

    const instructionsYesSymbolAsset = merge({}, makeNewAsset(AssetType.Symbol, width, height), {
        properties: {
            valign: 'center',
            halign: 'center',
            color: '#000',
            symbol: 'door-open',
        },
    });
    const instructionsYesSymbolContainer = makeNewContainer({
        id: 'ecs-instructions-yes-symbol',
        top: piHeight + messagesHeight + .06,
        left: 0,
        width: .5,
        height: instructionsHeight - .08,
        displayName: t('THEME_EDITOR.ECS.YES_SYMBOL_AREA_NAME'),
        type: 'ecs',
        assetOrder: [ instructionsYesSymbolAsset.id ],
        locked: true,
    });

    const instructionsNoAsset = merge({}, makeNewAsset(AssetType.Text, width, height), {
        properties: {
            valign: 'top',
            halign: 'center',
            color: '#000',
            text: 'Press for NO',
            size: .03,
        },
    });
    const instructionsNoContainer = makeNewContainer({
        id: 'ecs-instructions-no',
        top: piHeight + messagesHeight,
        left: .5,
        width: .5,
        height: instructionsHeight,
        displayName: t('THEME_EDITOR.ECS.NO_AREA_NAME'),
        type: 'ecs',
        backgroundColor: '#bfbfbf',
        borderColor: '#bfbfbf',
        borderThickness: Math.round(.015 * height),
        assetOrder: [ instructionsNoAsset.id ],
        locked: true,
    });

    const instructionsNoSymbolAsset = merge({}, makeNewAsset(AssetType.Symbol, width, height), {
        properties: {
            valign: 'center',
            halign: 'center',
            color: '#000',
            symbol: 'door-close',
        },
    });
    const instructionsNoSymbolContainer = makeNewContainer({
        id: 'ecs-instructions-no-symbol',
        top: piHeight + messagesHeight + .06,
        left: .5,
        width: .5,
        height: instructionsHeight - .08,
        displayName: t('THEME_EDITOR.ECS.NO_SYMBOL_AREA_NAME'),
        type: 'ecs',
        assetOrder: [ instructionsNoSymbolAsset.id ],
        locked: true,
    });

    const messagesContainer = makeNewContainer({
        id: 'ecs-messages',
        top: piHeight,
        left: 0,
        width: 1,
        height: messagesHeight,
        displayName: t('THEME_EDITOR.ECS.MESSAGES_AREA_NAME'),
        type: 'ecs',
        backgroundColor: '#fff',
        assetOrder: [],
        locked: true,
    });


    /* TODO: Emergency broadcast containers
        <div id="2eb665bc-37b8-45d0-806d-a4316d0f8a78" class="container ts-fade container-fake-broadcast" style="top:0px;left:280px;width:744px;height:80px;background:#000;color:#fff; font-size: 42px; line-height: 80px; z-index: 100; opacity: 0; font-family: sans-serif; text-align: center; font-weight: 700;">EMERGENCY ALERT</div>
        <div id="45a05685-83db-44d5-8101-d3dc99dae649" class="container ts-fade container-fake-broadcast" style="top:80px;left:280px;width:744px;height:520px; background:#c00; color:#fff; z-index: 100; opacity: 0; white-space: pre-wrap; font-size: 32px; text-align: center; font-family: sans-serif; padding: 30px; line-height: 38px;"></div>
    */

    return {
        ecsContainers: [
            backgroundContainer,
            statusMessageContainer,
            directionContainer,
            floorContainer,
            instructionsYesContainer,
            instructionsYesSymbolContainer,
            instructionsNoContainer,
            instructionsNoSymbolContainer,
            messagesContainer,
        ],
        ecsAssets: {
            [statusMessageAsset.id]: statusMessageAsset,
            [directionAsset.id]: directionAsset,
            [floorAsset.id]: floorAsset,
            [backgroundAsset.id]: backgroundAsset,
            [instructionsYesAsset.id]: instructionsYesAsset,
            [instructionsYesSymbolAsset.id]: instructionsYesSymbolAsset,
            [instructionsNoAsset.id]: instructionsNoAsset,
            [instructionsNoSymbolAsset.id]: instructionsNoSymbolAsset,
        },
    };
}
